import {equipment} from "../../../models/equipment";
import {NavLink, useNavigate} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {useForm, Controller, useFieldArray} from "react-hook-form";
import {UploadService} from "../../../service/UploadService";
import {ApiError, ApiQueryParam, ApiService} from "../../../service/ApiService";
import {scrollTo} from "../../../service/ToolsService";
import {Require} from "../Require";
import {collection} from "../../../models/collection";
import {collectionResultCollectionType, collectionResultType} from "../../../types/Api/CollectionResultType";
import {collectionHydrator} from "../../../models/hydrator/collectionHydrator";
import {Loader} from "../Loader";
import {size} from "../../../models/size";
import {sizeResultCollectionType, sizeResultType} from "../../../types/Api/SizeResultType";
import {sizeHydrator} from "../../../models/hydrator/sizeHydrator";
import {equipmentResultType} from "../../../types/Api/EquipmentResultType";
import {bodyArea} from "../../../models/bodyArea";
import {bodyAreaResultCollectionType, bodyAreaResultType} from "../../../types/Api/BodyAreaResultType";
import {bodyAreaHydrator} from "../../../models/hydrator/bodyAreaHydrator";

type SizeFormDataType = {
  equipmentSizeId?: string,
  sizeId?: string,
  stock?: number,
}

type FormDataType = {
  label: string,
  price?: number,
  collection?: string,
  bodyArea?: string,
  picture?: FileList,
  equipmentSize: SizeFormDataType[],
}

type EquipmentFormProp = {
  equipment?: equipment
}

export const EquipmentForm = ({equipment}: EquipmentFormProp) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [collectionOptions, setCollectionOptions] = useState([] as collection[]);
  const [bodyAreaOptions, setBodyAreaOptions] = useState([] as bodyArea[]);
  const [sizes, setSize] = useState([] as size[]);
  const [updateImage, setUpdateImage] = useState(false);
  const [deleteImage, setDeleteImage] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: {errors}
  } = useForm<FormDataType>({
    defaultValues: {
      label: equipment?.label,
      price: equipment?.price,
      collection: equipment?.collection.collectionId,
      bodyArea: equipment?.bodyArea?.bodyAreaId,
      equipmentSize: equipment?.sizes?.map((size: size) => {
        return {label: size.label, sizeId: size.sizeId, stock: size.stock, equipmentSizeId: size.equipmentSizeId}
      }),
    }
  });

  const {fields, append, remove} = useFieldArray({
    control,
    name: "equipmentSize",
  });

  useEffect(() => {
    const resultCollection = ApiService.get(`/collection`) as Promise<collectionResultCollectionType>;

    setLoading(true);

    const apiFiltersCollection: ApiQueryParam = new ApiQueryParam();

    apiFiltersCollection.setCount(0);
    apiFiltersCollection.addSort({
      by: "label",
      direction: "ASC"
    });

    setLoading(true);

    resultCollection
      .then((r) => {
        setCollectionOptions(r._embedded.collections.map((collection: collectionResultType) => {
          return collectionHydrator.hydrate(collection);
        }));
      })
      .catch(() => {
        alert("Une erreur c'est produite.")
      })
      .finally(() => {
        setLoading(false);
      });

    const apiFiltersBodyArea: ApiQueryParam = new ApiQueryParam();

    apiFiltersBodyArea.setCount(0);
    apiFiltersBodyArea.addSort({
      by: "label",
      direction: "ASC"
    });

    const resultBodyArea = ApiService.get(`/body-area`, apiFiltersBodyArea) as Promise<bodyAreaResultCollectionType>;

    setLoading(true);

    resultBodyArea
      .then((r) => {
        setBodyAreaOptions(r._embedded["body-areas"].map((bodyArea: bodyAreaResultType) => {
          return bodyAreaHydrator.hydrate(bodyArea);
        }));
      })
      .catch(() => {
        alert("Une erreur c'est produite.")
      })
      .finally(() => {
        setLoading(false);
      });

    const apiFiltersSize: ApiQueryParam = new ApiQueryParam();

    apiFiltersSize.setCount(0);
    apiFiltersSize.addSort({
      by: "order",
      direction: "ASC"
    });

    const resultSize = ApiService.get(`/size`, apiFiltersSize) as Promise<sizeResultCollectionType>;

    setLoading(true);

    resultSize
      .then((r) => {
        setSize(r._embedded.sizes.map((size: sizeResultType) => {
          return sizeHydrator.hydrate(size);
        }));
      })
      .catch(() => {
        alert("Une erreur c'est produite.")
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const onSubmit = async (data: FormDataType) => {
    let picture: undefined | string = undefined;

    if (deleteImage) {
      picture = '';
    } else if (data.picture && data.picture.length > 0) {
      picture = await UploadService.handleFileToBase64(data.picture[0]);
    }

    const toDelete = equipment?.sizes?.filter(size =>
      !data.equipmentSize.some(dSize => dSize.equipmentSizeId === size.equipmentSizeId)
    );

    const apiData = {
      label: data.label,
      price: data.price,
      collection: data.collection,
      bodyAreaId: data.bodyArea,
      picture: picture,
    };

    try {
      const result = equipment
        ? await ApiService.patch(`/equipment/${equipment.equipmentId}`, {param: () => apiData}) as equipmentResultType
        : await ApiService.post('/equipment', {param: () => apiData}) as equipmentResultType;

      for (const del of toDelete ?? []) {
        await ApiService.delete(`/equipment-size/${del.equipmentSizeId}`);
      }

      for (const dataES of data.equipmentSize) {
        let dES: {equipment_id?: string, size_id?: string, stock: number} = {
          stock: dataES.stock as number,
          size_id: dataES.sizeId as string,
        }

        if (dataES.equipmentSizeId) {
          await ApiService.patch(`/equipment-size/${dataES.equipmentSizeId}`, {param: () => dES});
        } else {
          dES.equipment_id = result.equipmentId;

          await ApiService.post('/equipment-size', {param: () => dES});
        }
      }

      navigate('/admin/equipment/equipment');
      scrollTo();
    } catch (e: any) {
      const error: ApiError = e as ApiError;

      error.errors?.forEach((e: {
        field: string,
        message: string
      }) => {
        setError(e.field as "label" | "price" | "collection" | "bodyArea", {
          type: "manual",
          message: e.message
        })
      });
    }
  }

  return (
    <>
      {loading && (
        <Loader/>
      )}

      {!loading && (
        <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex flex-wrap">
            <div className="col min-w400px max-w600px pe-4">
              <div className="mb-3">
                <label className="form-label" htmlFor="form-name">Nom <Require/></label>
                <input id="form-name" type="text" className="form-control" placeholder="~" {...register('label', {
                  required: 'Le nom est obligatoire',
                  validate: {
                    minLength: (value) =>
                      (value ?? '').length <= 255 || 'Le nom ne doit pas dépasser 255 caractères',
                  },
                })} />
                {errors.label && <p className="text-danger">{errors.label.message}</p>}
              </div>
              <div className="mb-3 d-flex flex-wrap justify-content-between">
                <div className="col min-w150px max-w200px me-1">
                  <label className="form-label" htmlFor="form-price">Collection <Require/></label>
                  <Controller
                    name="collection"
                    control={control}
                    defaultValue=""
                    rules={{required: 'La collection est obligatoire'}}
                    render={({field}) => (
                      <select {...field} className="form-select">
                        <option value="" disabled>~</option>
                        {collectionOptions.map(collectionOption => (
                          <option key={collectionOption.collectionId} value={collectionOption.collectionId}>
                            {collectionOption.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />

                  {errors.collection && <p className="text-danger">{errors.collection.message}</p>}
                </div>
                <div className="col min-w150px max-w300px">
                  <label className="form-label" htmlFor="form-price">Zone du corps <Require/></label>
                  <Controller
                    name="bodyArea"
                    control={control}
                    defaultValue=""
                    rules={{required: 'La zone du corps est obligatoire'}}
                    render={({field}) => (
                      <select {...field} className="form-select">
                        <option value="" disabled>~</option>
                        {bodyAreaOptions.map(bodyAreaOption => (
                          <option key={bodyAreaOption.bodyAreaId} value={bodyAreaOption.bodyAreaId}>
                            {bodyAreaOption.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />

                  {errors.collection && <p className="text-danger">{errors.collection.message}</p>}
                </div>
              </div>
              <div className="mb-3 max-w150px ">
                <label className="form-label" htmlFor="form-price">Prix <Require/></label>

                <div className="input-group mb-2 mr-sm-2">
                  <input id="form-price" type="number" className="form-control" placeholder="~" {...register('price', {
                    setValueAs: (value) => (value === "" ? undefined : Number(value)),
                    required: 'Le prix est obligatoire',
                    validate: {
                      valide: (value) =>
                        value === undefined || !Number.isNaN(value) || "Le prix doit être un chiffre",
                    },
                  })} />
                  <div className="input-group-text">€</div>
                </div>
                {errors.price && <p className="text-danger">{errors.price.message}</p>}
              </div>
              <div className="mb-3">
                <table className="table table-responsive border">
                  <thead>
                  <tr className="table-primary">
                    <th>Taille</th>
                    <th className="w100px">Stock</th>
                    <th className="w100px"></th>
                  </tr>
                  </thead>
                  <tbody>
                  {fields.map((field, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          <Controller
                            key={field.id}
                            control={control}
                            name={`equipmentSize.${index}.sizeId`}
                            rules={{required: 'La taille est obligatoire',}}
                            render={({field}) => (
                              <select {...field} className="form-select">
                                <option value="">~</option>
                                {
                                  sizes.map((size: size, i) => (
                                    <option key={i} value={size.sizeId}>{size.label}</option>
                                  ))
                                }
                              </select>
                            )}
                          />
                          {errors.equipmentSize?.[index]?.sizeId && <p className="text-danger">{errors.equipmentSize?.[index]?.sizeId.message}</p>}
                        </td>
                        <td>
                          <input id="form-label" type="text" className="form-control" placeholder="~" {...register(`equipmentSize.${index}.stock`, {
                            required: 'Le stock est obligatoire',
                            validate: {
                              valide: (value) =>
                                value === undefined || !Number.isNaN(value) || "Le stock doit être un chiffre",
                            },
                          })} />
                          {errors.equipmentSize?.[index]?.stock && <p className="text-danger">{errors.equipmentSize?.[index]?.stock.message}</p>}
                        </td>
                        <td className="text-end align-middle">
                          <button className="btn btn-xs btn-danger" type="button" onClick={() => remove(index)}>
                            Supprimer
                          </button>
                          <input
                            type="hidden"
                            {...register(`equipmentSize.${index}.equipmentSizeId`)}
                            value={undefined}
                          />
                        </td>
                      </tr>
                    )
                  })}
                  </tbody>
                </table>
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-xs btn-primary"
                    onClick={() => append({sizeId: undefined, stock: undefined, equipmentSizeId: undefined})}
                  >
                    Ajouter une taille
                  </button>
                </div>
              </div>
            </div>
            <div className="col min-w400px max-w600px pe-4">
              <div className="mb-3">
                <label htmlFor="formFile" className="form-label d-block">Picture</label>
                {equipment?.picture && (
                  <>
                    {!updateImage && !deleteImage && (
                      <>
                        <img src={equipment.picture} className="max-h250px img-fluid d-block mb-3" alt={equipment.label}/>
                        <button className="btn btn-xs btn-primary me-1" onClick={() => setUpdateImage(true)}>modifier</button>
                        <button className="btn btn-xs btn-danger me-1" onClick={() => setDeleteImage(true)}>supprimer</button>
                      </>
                    )}

                    {updateImage && (
                      <>
                        <label className="form-label italic me-2">si renseignée remplacera l'actuelle</label>
                        <button className="btn btn-xs btn-info me-1" onClick={() => setUpdateImage(false)}>annuler</button>
                      </>
                    )}
                    {deleteImage && (
                      <>
                        <label className="form-label italic me-2">l'image sera supprimée lors de l'enregistrement</label>
                        <button className="btn btn-xs btn-info me-1" onClick={() => setDeleteImage(false)}>annuler</button>
                      </>
                    )}
                  </>
                )}
                {(updateImage || !equipment?.picture) && (
                  <>
                    <input className="form-control form-control-sm" type="file" id="formFile" {...register('picture', {
                      validate: {
                        requiredIfUpdate: (value) =>
                          !updateImage || (value ?? []).length > 0 || 'L\'image est requise',
                      },
                    })}/>
                    {errors.picture && <p className="text-danger">{errors.picture.message}</p>}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <button className="btn btn-sm btn-success me-1" type="submit">Enregistrer</button>
            <NavLink to={`/admin/equipment/equipment`} onClick={scrollTo} className="btn btn-sm btn-info">Retour aux équipements</NavLink>
          </div>
        </form>
      )}
    </>
  )
}
